import { Injectable, OnDestroy, Provider, Type, inject } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { Subject } from 'rxjs/internal/Subject';
import { NProductConfiguration } from '../df-module-product-configuration.interfaces';
import { ProductConfigurationBaseService } from '../model/base/product-configuration-base.service';
import { ProductConfigurationBundle } from '../model/bundle/product-configuration-bundle';
import { ProductConfigurationConfigurable } from '../model/configurable/product-configuration-configurable';
import { ProductConfigurationSimple } from '../model/simple/product-configuration-simple';
import { ProductConfigurationVirtual } from '../model/virtual/product-configuration-virtual';
import { ProductConfigurationConfigurableOptionsService } from '../model/configurable/options/product-configuration-configurable-options.service';
import { ProductConfigurationConfigurableStorageService } from '../model/configurable/storage/product-configuration-configurable-storage.service';
import { ProductConfigurationConfigurableProductDataService } from '../model/configurable/product-data/product-configuration-configurable-product-data.service';
import { ProductConfigurationCustomOptionsService } from '../model/simple/custom-options/product-configuration-configurable-product-data.service';
import { ProductConfigurationBundleOptionService } from '../model/bundle/options/product-configuration-bundle-option.service';

export const instances = {
    simple: ProductConfigurationSimple,
    configurable: ProductConfigurationConfigurable,
    bundle: ProductConfigurationBundle,
    virtual: ProductConfigurationVirtual
};

export interface IProductComponent<TProduct extends Product<IProductDataV3> = Product<IProductDataV3>> {
    model: TProduct;
    detectChanges: () => void;
}

export function getProductConfigurationProviders(setup: {
    component: Type<IProductComponent>;
    /**
     * If yes then url and other meta of the whole page will be updated
     */
    metaIntegration: boolean;
    metaIntegrationAlwaysUpdateUrl?: boolean;
    bundle?: () => Partial<NProductConfiguration.Bundle.Config>;
    configurable?: () => Partial<NProductConfiguration.Configurable.Config>;
    localProviders?: Provider[];
}): Provider[] {
    let instance: ProductConfiguration;
    return [
        UnsubscribingService,
        ProductConfigurationBaseService,
        ProductConfigurationConfigurableOptionsService,
        ProductConfigurationConfigurableStorageService,
        ProductConfigurationConfigurableProductDataService,
        ProductConfigurationCustomOptionsService,
        ProductConfigurationBundleOptionService,
        ...(ProductConfiguration.plugins as any[]),
        ...(setup.localProviders || []),
        { provide: NProductConfiguration.Configuration, useFactory: () => instance }, // trick to inject to engines
        {
            provide: ProductConfiguration,
            useFactory: unsubscribingService => {
                const component = inject(setup.component) as IProductComponent;
                const product = component.model!;
                const produdctType = product.parent?.isConfigurable ? 'configurable' : product.data?.type_id || 'simple';
                instance = new instances[produdctType]();
                const modelChangeSubscription = instance.product$.subscribe(product => {
                    if (product && component.model !== product) {
                        component.model = product;
                        component.detectChanges();
                    }
                });
                const subscription = unsubscribingService.destroyed$.subscribe(() => {
                    modelChangeSubscription?.unsubscribe();
                    instance.ngOnDestroy();
                    setTimeout(() => {
                        subscription?.unsubscribe();
                    });
                });
                (instance as any).init(
                    product,
                    { metaIntegration: setup.metaIntegration, metaIntegrationAlwaysUpdateUrl: setup.metaIntegrationAlwaysUpdateUrl },
                    setup[produdctType]?.()
                );
                return instance;
            },
            deps: [UnsubscribingService]
        }
    ];
}

@Injectable()
export class UnsubscribingService implements OnDestroy {
    readonly destroyed$ = new Subject<void>();

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
